import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ListView from '@/components/ListView';
import { Loader } from '@/components/ui/loader';
import { Footer } from '@/components/Footer';
import { fetchPost, fetchAllCategories, markPostfavorite, type Category } from '@/services/api';
import type { Post } from '@/types';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

const PostDetailLayout = ({
  children,
  isLoading = false,
  onBackToHome,
}: {
  children: React.ReactNode;
  isLoading?: boolean;
  onBackToHome: () => void;
}) => (
  <div className="min-h-screen bg-gray-900">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen flex flex-col bg-gray-900"
    >
      {/* Top Header Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Back button visible only on mobile */}
            <button
              className="md:hidden bg-transparent flex items-center p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              onClick={() => !isLoading && onBackToHome()}
              disabled={isLoading}
              aria-label="Back to Home"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-white">Instavault</h1>
            <span
              data-slot="badge"
              className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 bg-purple-500/20 text-purple-300 border-purple-500/30"
            >
              Post Detail
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row">
        {/* Left Sidebar — hidden on mobile */}
        <div className="hidden md:flex w-64 flex-shrink-0 bg-slate-900/30 p-6 border-r border-gray-700 flex-col">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 tracking-wider uppercase">
            Post Detail
          </h3>
          <div className="space-y-2">
            <div
              className="w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center space-x-3 bg-purple-500/20 text-white cursor-pointer"
              onClick={() => !isLoading && onBackToHome()}
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Home</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-slate-900/30 p-3 sm:p-6 overflow-auto min-h-0">
          {children}
        </div>
      </div>
    </motion.div>
    <Footer />
  </div>
);

export const PostDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Missing post ID');
      setLoading(false);
      return;
    }
    const postId = Number(id);
    if (Number.isNaN(postId) || postId <= 0) {
      setError('Invalid post ID');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([fetchPost(postId), fetchAllCategories()])
      .then(([postData, categoriesData]) => {
        if (!cancelled) {
          setPost(postData);
          setCategories(categoriesData);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load post');
          toast.error('Failed to load post');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleCategoryUpdate = async (_postId: number) => {
    if (!id) return;
    const postId = Number(id);
    if (Number.isNaN(postId)) return;
    try {
      const updated = await fetchPost(postId);
      setPost(updated);
    } catch {
      // Keep current post on error
    }
  };

  const handleFavouriteToggle = async (postId: number, isFavourite: boolean) => {
    try {
      await markPostfavorite(postId, isFavourite);
      setPost((prev) => (prev ? { ...prev, is_favorite: isFavourite } : null));
    } catch (error) {
      console.error('Failed to toggle favourite:', error);
      toast.error('Failed to update favourite. Please try again.');
    }
  };

  if (loading) {
    return (
      <PostDetailLayout isLoading onBackToHome={() => navigate('/')}>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader />
        </div>
      </PostDetailLayout>
    );
  }

  if (error || !post) {
    return (
      <PostDetailLayout onBackToHome={() => navigate('/')}>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-gray-300">{error ?? 'Post not found'}</p>
          <Button
            variant="outline"
            onClick={() => navigate('/', { replace: true })}
            className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to home
          </Button>
        </div>
      </PostDetailLayout>
    );
  }

  return (
    <PostDetailLayout onBackToHome={() => navigate('/')}>
      <ListView
        reels={[post]}
        loading={false}
        initialLoading={false}
        isBlurred={false}
        onLoadMore={() => {}}
        hasMore={false}
        onCategoryUpdate={handleCategoryUpdate}
        onPostDelete={() => {
          toast.success('Post deleted');
          navigate('/', { replace: true });
        }}
        onPostArchive={() => {
          toast.success(post.is_archived ? 'Post unarchived' : 'Post archived');
          navigate('/', { replace: true });
        }}
        onFavouriteToggle={handleFavouriteToggle}
        favouriteStates={post ? { [post.id.toString()]: post.is_favorite ?? false } : {}}
        categories={categories}
        showFullCaption
      />
    </PostDetailLayout>
  );
};
